import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { roomTypeFor } from "../lib/roomLinen";
import { DoorOpen, Trash2 } from "lucide-react";

export function RoomLinenParPanel() {
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [rows, setRows] = useState([]);          // {id, label, item_id, matched, typePar, override, value}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedRoom = rooms.find(r => r.id === roomId);
  const selectedRoomType = roomTypes.find(type => (
    type.id === selectedRoom?.room_type_id || type.name === selectedRoom?.room_type
  ));
  const roomType = selectedRoomType?.name || selectedRoom?.room_type || (
    selectedRoom ? roomTypeFor(selectedRoom.number) : null
  );
  const roomTypeId = selectedRoomType?.id || selectedRoom?.room_type_id || "";

  useEffect(() => {
    (async () => {
      const [roomsResponse, typesResponse] = await Promise.all([
        api.get("/rooms"),
        api.get("/room-types"),
      ]);
      setRooms(roomsResponse.data);
      setRoomTypes(typesResponse.data);
    })();
  }, []);

  useEffect(() => {
    if (!roomId || !roomTypeId) { setRows([]); return; }
    (async () => {
      const [parResponse, templateResponse] = await Promise.all([
        api.get(`/rooms/${roomId}/linen-par`),
        api.get(`/room-types/${roomTypeId}/linen-template`),
      ]);
      const typePar = parResponse.data.type_par || {};
      const overrides = parResponse.data.overrides || {};
      setRows(templateResponse.data.map(template => {
        const iid = template.item_id || null;
        const override = iid && overrides[iid] != null ? overrides[iid] : null;
        const tp = iid && typePar[iid] != null ? typePar[iid] : null;
        const effective = override != null ? override : tp;
        return {
          id: template.id,
          label: template.item_name || template.legacy_label,
          item_id: iid,
          matched: !template.orphaned,
          typePar: tp,
          override,
          value: effective != null ? String(effective) : "",
        };
      }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, roomTypeId]);

  const setValue = (idx, v) => setRows(rows.map((r, i) => i === idx ? { ...r, value: v } : r));

  const save = async () => {
    const entries = [];
    for (const r of rows) {
      if (!r.matched || !r.item_id) continue;
      const raw = (r.value ?? "").trim();
      if (raw === "") continue; // blank = leave unchanged
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(`"${r.label}" must be a whole number 0 or more`);
        return;
      }
      entries.push({ item_id: r.item_id, par_qty: n });
    }
    if (entries.length === 0) { toast.error("Enter at least one par value to save"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/rooms/${roomId}/linen-par`, { entries, reason: reason || null });
      toast.success(`Saved ${data.changed} change(s) for Room ${selectedRoom.number}`);
      setReason("");
      // reload
      const { data: rp } = await api.get(`/rooms/${roomId}/linen-par`);
      setRows(rows.map(r => {
        const ov = r.item_id && rp.overrides?.[r.item_id] != null ? rp.overrides[r.item_id] : null;
        return { ...r, override: ov };
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyToType = async () => {
    const sameType = rooms.filter(room => (
      room.id !== roomId && room.room_type_id === roomTypeId
    ));
    if (sameType.length === 0) { toast.error("No other rooms of this type"); return; }
    if (!window.confirm(`Copy Room ${selectedRoom.number}'s par values to ${sameType.length} other "${roomType}" room(s): ${sameType.map(r => r.number).join(", ")}?\n\nThis only sets reference par values — no stock is moved.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/linen-par/copy-to-type`);
      toast.success(`Copied to ${data.count} room(s): ${(data.affected || []).join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const deleteRow = async (row) => {
    if (!row.id || !roomTypeId) return;
    const confirmed = window.confirm(
      `Delete "${row.label}" from the "${roomType}" room-type template?\n\n` +
      `This removes it from every "${roomType}" room's par table, not just Room ${selectedRoom.number}. ` +
      `The matching room-type par value (if any) is removed too. Per-room saved values are left in place and ` +
      `will reappear only if the template entry is re-added.`
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/room-types/${roomTypeId}/linen-template/${encodeURIComponent(row.id)}`);
      toast.success(`Removed "${data.item_name || row.label}" from the ${roomType} template`);
      // Refetch template + par to reflect the change immediately
      const [parResponse, templateResponse] = await Promise.all([
        api.get(`/rooms/${roomId}/linen-par`),
        api.get(`/room-types/${roomTypeId}/linen-template`),
      ]);
      const typePar = parResponse.data.type_par || {};
      const overrides = parResponse.data.overrides || {};
      setRows(templateResponse.data.map(template => {
        const iid = template.item_id || null;
        const override = iid && overrides[iid] != null ? overrides[iid] : null;
        const tp = iid && typePar[iid] != null ? typePar[iid] : null;
        const effective = override != null ? override : tp;
        return {
          id: template.id,
          label: template.item_name || template.legacy_label,
          item_id: iid,
          matched: !template.orphaned,
          typePar: tp,
          override,
          value: effective != null ? String(effective) : "",
        };
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="font-semibold text-slate-900">Room Linen Par</h2>
        <p className="text-sm text-slate-500">Set reference par quantities for an individual room. These are expected values only — no stock is moved.</p>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Room</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger className="h-11 mt-1" data-testid="par-room-select"><SelectValue placeholder="Select a room" /></SelectTrigger>
              <SelectContent>
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id}>
                    Room {room.number}{roomTypes.find(type => type.id === room.room_type_id)?.name
                      ? ` — ${roomTypes.find(type => type.id === room.room_type_id).name}`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            {selectedRoom && (roomType
              ? <Badge className="bg-slate-900 text-white flex items-center gap-1"><DoorOpen className="w-3 h-3" /> {roomType}</Badge>
              : <Badge className="bg-amber-100 border text-amber-800">Room type not mapped — configure in Admin Settings</Badge>)}
          </div>
        </div>
      </Card>

      {selectedRoom && roomType && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto"><table className="vr-table">
            <thead><tr><th>Linen item</th><th className="num">Room-type Par</th><th className="num">Current Room Par</th><th className="num">New Par</th><th className="num"></th></tr></thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id || r.label}>
                  <td>{r.label}{!r.matched && <Badge className="ml-2 bg-slate-100 border text-slate-500 text-[10px]">needs mapping</Badge>}</td>
                  <td className="num">{r.typePar != null ? r.typePar : <span className="text-slate-400 text-xs">Not Configured</span>}</td>
                  <td className="num">{r.override != null ? <span className="font-semibold text-emerald-700">{r.override}</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="num">
                    <Input type="number" min="0" step="1" value={r.value} disabled={!r.matched}
                      onChange={e => setValue(idx, e.target.value)}
                      className="h-9 w-24 text-right" data-testid={`par-input-${r.label}`} />
                  </td>
                  <td className="num">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      onClick={() => deleteRow(r)} disabled={busy || !r.id}
                      title={`Remove "${r.label}" from the ${roomType} template`}
                      data-testid={`par-delete-${r.label}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="p-4 border-t space-y-3">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why are these par values being set?" className="mt-1" data-testid="par-reason" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={save} disabled={busy} data-testid="par-save-btn">Save Par Values</Button>
              <Button variant="outline" onClick={copyToType} disabled={busy} data-testid="par-copy-btn">Copy to all {roomType} rooms</Button>
            </div>
            <p className="text-xs text-slate-400">Blank leaves a value unchanged. Enter 0 if the item is not needed in this room. Whole numbers only.</p>
          </div>
        </Card>
      )}
    </>
  );
}
